<template>
    <DsModalDialog
        :cssClass="[
            'ds-modal-dialog--video',
            canShowVideo ? 'ds-modal-dialog--gdpr-consented' : '',
            props.isYoutubeShort ? 'ds-modal-dialog--youtube-short' : '',
        ]"
        :modelValue="isModalVisible"
        modalPosition="center"
        :animate="false"
        :theme="theme"
        @update:model-value="handleModalClosed"
        ref="dialogRef"
        trap-focus
    >
        <template #content>
            <iframe
                v-if="canShowVideo"
                :src="embedVideoUrl"
                title="Video"
                class="ds-modal-dialog__iframe"
                allowfullscreen
                allow="autoplay; encrypted-media"
                loading="lazy"
            ></iframe>
            <DsModalVideoGdprOverlay v-else :videoUrl="embedVideoUrl" ref="overlayRef" />
        </template>
    </DsModalDialog>
</template>

<script setup lang="ts">
import { getCurrentInstance, nextTick, onMounted, onUnmounted, onUpdated, ref } from "vue";
import { useWecomaIntegration } from "@allwyn/gdpr/utils/use-wecoma-integration";
import { debounce } from "@allwyn/utils/debounce";
import { eventBus, GlobalEvents } from "@allwyn/utils/global-events";
import DsModalVideoGdprOverlay from "./ds-modal-video-gdpr-overlay.vue";
import { DsModalVideoProps } from "./ds-modal-video-props";
import { useDsModalVideoTools } from "./use-ds-modal-video-tools";
import DsModalDialog from "../modal-dialog/ds-modal-dialog.vue";

const props = withDefaults(defineProps<DsModalVideoProps>(), {
    theme: "light",
    cssClass: "",
});

const app = getCurrentInstance();
const overlayRef = ref<InstanceType<typeof DsModalVideoGdprOverlay> | null>(null);
const dialogRef = ref<InstanceType<typeof DsModalDialog> | null>(null);
const scrollHeight = ref(0);
const isModalVisible = ref(false);
const canShowVideo = ref(false);
const { embedVideoUrl } = useDsModalVideoTools(props.videoCodeOrUrl);
const { thirdPartyConsented } = useWecomaIntegration();

const checkForConsent = async () => {
    const consented = await thirdPartyConsented();
    canShowVideo.value = consented;
};

const handleModalClosed = () => {
    nextTick(() => {
        app.appContext.app.unmount();
    });
};

function setMinHeight() {
    if (!dialogRef.value?.dialogBodyRef) return;

    dialogRef.value?.dialogBodyRef?.style.setProperty(
        "min-height",
        `calc(${scrollHeight.value ?? 0}px + calc(var(--sp-dynamic-300, 24px) * 2))`
    );
}

function handleResize() {
    scrollHeight.value = overlayRef.value?.gdprOverlayContent.scrollHeight;
    setMinHeight();
}

const handleResizeDebounced = debounce(handleResize, 50);

onUpdated(() => {
    scrollHeight.value = overlayRef.value?.gdprOverlayContent.scrollHeight;
    setMinHeight();
});

onMounted(() => {
    checkForConsent();

    window.addEventListener("resize", handleResizeDebounced);
    isModalVisible.value = true;
    eventBus.on(GlobalEvents.ConsentsChanged, checkForConsent);
});

onUnmounted(() => {
    eventBus.off(GlobalEvents.ConsentsChanged, checkForConsent);
    window.removeEventListener("resize", handleResizeDebounced);
});
</script>
